'use client';

import React from 'react';
import * as RadixContextMenu from '@radix-ui/react-context-menu';
import styled from 'styled-components';
import { focusRing } from '@liftango/ui-tokens';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ContextMenuProps {
  /** Called when the open state changes. */
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

export interface ContextMenuTriggerProps {
  /** The element that receives the right-click. Rendered via `asChild`. */
  children: React.ReactElement;
}

export interface ContextMenuContentProps {
  /** Offset along the alignment axis in px. Defaults to 0. */
  alignOffset?: number;
  children: React.ReactNode;
}

export interface ContextMenuItemProps {
  /** Primary/affirmative-action variant — rendered in brand colour. */
  $primary?: boolean;
  /** Right-aligned monospace hint (keyboard shortcut, etc.). */
  hint?: React.ReactNode;
  /** Called when the item is selected (click or Enter). */
  onSelect?: (event: Event) => void;
  /** Prevent the menu from closing when this item is selected. */
  disabled?: boolean;
  children: React.ReactNode;
}

export interface ContextMenuSeparatorProps {
  className?: string;
}

export interface ContextMenuLabelProps {
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const StyledContent = styled(RadixContextMenu.Content)`
  background: ${(p) => p.theme.colors.neutral.panel};
  border: 1px solid ${(p) => p.theme.colors.neutral.lineStrong};
  border-radius: ${(p) => p.theme.radius.md};
  /* theme.zIndex.popover (400) is reused here; a dedicated contextMenu slot may be warranted later */
  z-index: ${(p) => p.theme.zIndex.popover};
  box-shadow: ${(p) => p.theme.elevation.floating.shadow};
  padding: ${(p) => p.theme.spaceAlias.xs} 0;
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.sm};
  color: ${(p) => p.theme.colors.neutral.ink};
  min-width: 200px;
  outline: none;

  &[data-state='open'] {
    animation: contextMenuIn 120ms ease;
  }
  &[data-state='closed'] {
    animation: contextMenuOut 80ms ease;
  }

  @keyframes contextMenuIn {
    from {
      opacity: 0;
      transform: scale(0.97);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  @keyframes contextMenuOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
`;

const StyledItem = styled(RadixContextMenu.Item)<{ $primary?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${(p) => p.theme.spaceAlias.lg};
  padding: ${(p) => p.theme.spaceAlias.xs} ${(p) => p.theme.spaceAlias.md};
  cursor: pointer;
  border-radius: ${(p) => p.theme.radius.xs};
  color: ${(p) => (p.$primary ? p.theme.colors.brand.primary : p.theme.colors.neutral.ink)};
  font-weight: ${(p) => (p.$primary ? p.theme.fontWeight.semibold : p.theme.fontWeight.regular)};
  outline: none;
  user-select: none;

  &[data-highlighted] {
    background: ${(p) => p.theme.colors.neutral.bg};
    color: ${(p) => (p.$primary ? p.theme.colors.brand.primaryHover : p.theme.colors.neutral.ink)};
  }

  &[data-disabled] {
    color: ${(p) => p.theme.colors.neutral.inkFaint};
    cursor: not-allowed;
    pointer-events: none;
  }

  ${focusRing}
`;

const ItemLabel = styled.span`
  flex: 1;
`;

const ItemHint = styled.span`
  font-family: ${(p) => p.theme.fontFamily.mono};
  font-size: ${(p) => p.theme.fontSize.xs};
  color: ${(p) => p.theme.colors.neutral.inkFaint};
  flex-shrink: 0;
`;

const StyledSeparator = styled(RadixContextMenu.Separator)`
  height: 1px;
  background: ${(p) => p.theme.colors.neutral.line};
  margin: ${(p) => p.theme.spaceAlias.xs} 0;
`;

const StyledLabel = styled(RadixContextMenu.Label)`
  padding: ${(p) => p.theme.spaceAlias.xs} ${(p) => p.theme.spaceAlias.md};
  font-size: ${(p) => p.theme.fontSize.eyebrow};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  color: ${(p) => p.theme.colors.neutral.inkMute};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  user-select: none;
`;

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

/**
 * Right-click triggered positioned menu built on Radix ContextMenu.
 * Provides keyboard navigation, focus management, and collision-avoidance
 * without any manual x/y positioning.
 *
 * @example
 * <ContextMenu>
 *   <ContextMenu.Trigger>
 *     <div>Right-click me</div>
 *   </ContextMenu.Trigger>
 *   <ContextMenu.Content>
 *     <ContextMenu.Item onSelect={handleCopy}>Copy</ContextMenu.Item>
 *     <ContextMenu.Separator />
 *     <ContextMenu.Item $primary onSelect={handleDelete}>Delete</ContextMenu.Item>
 *   </ContextMenu.Content>
 * </ContextMenu>
 */
export function ContextMenu({ onOpenChange, children }: ContextMenuProps) {
  return <RadixContextMenu.Root onOpenChange={onOpenChange}>{children}</RadixContextMenu.Root>;
}

/**
 * The element that triggers the context menu on right-click. Rendered via
 * `asChild` so the trigger child is not wrapped in an extra DOM node.
 */
function Trigger({ children }: ContextMenuTriggerProps) {
  return <RadixContextMenu.Trigger asChild>{children}</RadixContextMenu.Trigger>;
}

/**
 * The menu panel. Portalled to `document.body` to escape overflow and
 * stacking contexts. Radix handles collision-avoidance automatically.
 */
function Content({ alignOffset = 0, children }: ContextMenuContentProps) {
  return (
    <RadixContextMenu.Portal>
      <StyledContent alignOffset={alignOffset}>{children}</StyledContent>
    </RadixContextMenu.Portal>
  );
}

/**
 * A single menu item. Fires `onSelect` on click or Enter.
 * Use `$primary` for the brand-coloured affirmative variant.
 * Use `hint` for right-aligned monospace shortcut labels.
 */
function Item({ $primary, hint, onSelect, disabled, children }: ContextMenuItemProps) {
  return (
    <StyledItem $primary={$primary} onSelect={onSelect} disabled={disabled}>
      <ItemLabel>{children}</ItemLabel>
      {hint !== undefined && <ItemHint>{hint}</ItemHint>}
    </StyledItem>
  );
}

/**
 * A thin horizontal rule between groups of items.
 */
function Separator({ className }: ContextMenuSeparatorProps) {
  return <StyledSeparator className={className} />;
}

/**
 * An optional non-interactive section heading.
 */
function Label({ children }: ContextMenuLabelProps) {
  return <StyledLabel>{children}</StyledLabel>;
}

// Attach sub-components
ContextMenu.Trigger = Trigger;
ContextMenu.Content = Content;
ContextMenu.Item = Item;
ContextMenu.Separator = Separator;
ContextMenu.Label = Label;
